/**
 * 404 Not Found Page
 * Professional error page with search and suggestions
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Home, FolderKanban, FileText, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { SEOHead } from '@/components/marketing/SEOHead';

export const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/blog?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const suggestions = [
    { icon: Home, label: 'Home', href: '/' },
    { icon: FolderKanban, label: 'Features', href: '/features' },
    { icon: FileText, label: 'Blog', href: '/blog' },
  ];

  return (
    <>
      <SEOHead
        title="Page Not Found | ReturnCatalyst"
        description="The requested ReturnCatalyst page could not be found."
        canonical="/404"
        noIndex
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full border-slate-200 shadow-xl">
          <CardContent className="p-8 md:p-12 text-center">
          {/* 404 */}
            <div className="mb-6">
              <h1 className="text-8xl md:text-9xl font-bold text-blue-600 opacity-20 mb-4">
                404
              </h1>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Page Not Found
              </h2>
              <p className="text-slate-600 max-w-md mx-auto">
                The page you're looking for doesn't exist or has been moved.
              </p>
            </div>

          {/* Search */}
            <form onSubmit={handleSearch} className="mb-8">
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search private equity AI resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </form>

          {/* Suggestions */}
            <div className="mb-8">
              <p className="text-sm font-semibold text-slate-700 mb-4">
                Try one of these:
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {suggestions.map((suggestion) => {
                  const Icon = suggestion.icon;
                  return (
                    <Button
                      key={suggestion.href}
                      onClick={() => navigate(suggestion.href)}
                      variant="outline"
                      className="gap-2"
                    >
                      <Icon className="h-4 w-4" />
                      {suggestion.label}
                    </Button>
                  );
                })}
              </div>
            </div>

          {/* Back Button */}
            <Button onClick={() => navigate(-1)} variant="ghost" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
